import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const EMPTY = {
  name: "",
  phone: "",
  tin: "",
  address: "",
  business: false,
  withholdingAgent: false,
  taxType: "VAT",
};

export default function PartnerQuickModal({
  open,
  type = "CUSTOMER", // CUSTOMER | SUPPLIER
  loading = false,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  if (!open) return null;

  const isCustomer = type === "CUSTOMER";

  const change = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = () => {
    if (!form.name.trim()) {
      alert(`${isCustomer ? "Customer" : "Supplier"} name is required.`);
      return;
    }

    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-xl bg-slate-900 shadow-xl">
        {/* Header */}

        <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">
            New {isCustomer ? "Customer" : "Supplier"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
          <div className="md:col-span-2">
            <label className="text-sm text-slate-300">
              {isCustomer ? "Customer" : "Supplier"} Name *
            </label>

            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm text-slate-300">Phone</label>

            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
              value={form.phone}
              onChange={(e) => change("phone", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm text-slate-300">TIN</label>

            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
              value={form.tin}
              onChange={(e) => change("tin", e.target.value)}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm text-slate-300">Address</label>

            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
              value={form.address}
              onChange={(e) => change("address", e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.business}
              onChange={(e) => {
                const checked = e.target.checked;

                setForm((prev) => ({
                  ...prev,
                  business: checked,
                  withholdingAgent: checked ? prev.withholdingAgent : false,
                  taxType: checked ? prev.taxType : "NONE",
                }));
              }}
            />

            <span className="text-slate-300">
              Business {isCustomer ? "Customer" : "Supplier"}
            </span>
          </div>

          {form.business && (
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-sm text-slate-300">Tax Scheme Type</label>
              <select
                value={form.taxType}
                onChange={(e) => change("taxType", e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="VAT">VAT (Value Added Tax)</option>
                <option value="TOT">TOT (Turnover Tax)</option>
                <option value="NONE">Exempt / Non-registered</option>
              </select>
            </div>
          )}

          {form.business && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.withholdingAgent}
                onChange={(e) => change("withholdingAgent", e.target.checked)}
              />

              <span className="text-slate-300">Withholding Agent</span>
            </div>
          )}
        </div>

        {/* Footer */}

        <div className="flex justify-end gap-3 border-t border-slate-700 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-600 px-5 py-2 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            disabled={loading}
            onClick={save}
            className="rounded-lg bg-emerald-600 px-5 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : `Save `}
          </button>
        </div>
      </div>
    </div>
  );
}
